import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, Building2, Users, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { IMAGES } from '../../constants/images';

const testimonials = [
  {
    quote: "TOF Consultancy brought clarity and precision to our subsidy application process. Their in-depth knowledge of government schemes made a significant difference for our hospitality project.",
    name: "Vivek Choudhary",
    designation: "Director",
    company: "Holiday Inn Express Jaipur",
    industry: "Hospitality",
    highlight: "IHG Hotels & Resorts",
    image: IMAGES.vivek
  },
  {
    quote: "TOF Consultancy's expertise in financial solutions has greatly benefited our operations at Akshat Apartments Pvt. Ltd. Their guidance has been invaluable in navigating complex financial landscapes.",
    name: "Ved Prakash Sujaka",
    designation: "DGM - Finance",
    company: "Akshat Apartments Private Limited",
    industry: "Real Estate",
    highlight: "One of Rajasthan's Premier Real Estate Developer",
    image: IMAGES.ved
  },
  {
    quote: "TOF Consultancy's financial guidance was instrumental in navigating our growth as an NSE listed medical devices company and exporter. Their expertise is unmatched.",
    name: "Uday Chandra Jha",
    designation: "Senior Manager",
    company: "Poly Medicure Limited",
    industry: "Medical Devices",
    image: IMAGES.uday
  },
  {
    quote: "We would like to thank team TOF for the work done for the organization. They are very clear what is required and made sure work was completed at the fastest pace in the shortest time. I wish the whole team all the best for future. I strongly recommend them to anyone seeking their services.",
    name: "Sukhet Gupta",
    designation: "Vice President",
    company: "Shri Krishna Rolling Mills Limited",
    industry: "Manufacturing",
    image: IMAGES.sukhet
  },
  {
    quote: "The team at TOF demonstrated exceptional professionalism in handling our financial requirements. Their guidance on RIPS subsidies helped us optimize our expansion plans.",
    name: "Mohit Soni",
    designation: "Director",
    company: "Soni Medicare Ltd",
    industry: "Healthcare",
    image: IMAGES.mohit
  },
  {
    quote: "Working with TOF has been a game-changer for our manufacturing unit. Their expertise in navigating government incentive schemes is truly commendable.",
    name: "Chetan M Mokzaria",
    designation: "Managing Director",
    company: "Minova Runaya Private Limited",
    industry: "Manufacturing",
    image: IMAGES.chetan
  },
  {
    quote: "TOF's comprehensive approach to subsidy consultation helped us secure significant government benefits. Their team's dedication and expertise are outstanding.",
    name: "Basant Kumar Jain",
    designation: "Director",
    company: "Shri Adinath Cashew Pvt Ltd",
    industry: "Food Processing",
    image: IMAGES.basant
  },
  {
    quote: "The professional guidance from TOF Consultancy made our subsidy application process smooth and successful. Highly recommended for any business seeking government benefits.",
    name: "Aayush Kogta",
    designation: "Director",
    company: "Kogta Housing Development",
    industry: "Real Estate",
    image: IMAGES.aayush
  }
];

export const ClientsTestimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const scrollToIndex = (index) => {
    if (scrollRef.current) {
      const cardWidth = 400;
      scrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const cardWidth = 400;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(newIndex);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <section className="py-24 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium text-[#00bcd4] mb-4">
            Client Testimonials
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white mb-4">
            What Our Clients Say
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Real feedback from real clients who have experienced our services.
          </p>
        </motion.div>

        <div className="relative">
          <button
            onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors hidden md:flex"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={() => scrollToIndex(Math.min(testimonials.length - 1, activeIndex + 1))}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors hidden md:flex"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-6 px-4 md:px-12 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex-shrink-0 w-[350px] md:w-[400px] snap-center"
              >
                <Card className="h-full bg-white/5 border-white/10 hover:bg-white/10 transition-colors overflow-hidden backdrop-blur">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-[#00bcd4]"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                          {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-white">{testimonial.name}</p>
                        <p className="text-slate-400 text-sm">{testimonial.designation}</p>
                        <p className="text-[#00bcd4] text-sm font-medium">{testimonial.company}</p>
                      </div>
                    </div>

                    {testimonial.highlight && (
                      <p className="text-[#00bcd4] font-medium text-xs mb-3 bg-[#00bcd4]/10 px-3 py-1 rounded-full inline-block">
                        {testimonial.highlight}
                      </p>
                    )}

                    <div className="relative">
                      <Quote className="w-8 h-8 text-[#00bcd4]/30 absolute -top-2 -left-2" />
                      <blockquote className="text-white/80 leading-relaxed text-sm pl-6 line-clamp-5">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>

                    <div className="flex gap-1 mt-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#00bcd4] text-[#00bcd4]" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeIndex ? 'bg-[#00bcd4] w-6' : 'bg-white/30'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto">
          {[
            { icon: <Building2 />, number: '500+', label: 'Clients Served' },
            { icon: <Award />, number: '₹1000Cr+', label: 'Funds Facilitated' },
            { icon: <Users />, number: '15+', label: 'Industries' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white">
                {stat.icon}
              </div>
              <p className="text-3xl font-semibold text-white font-mono mb-1">{stat.number}</p>
              <p className="text-slate-400 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
